import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, User } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * SMIN unified header — rendered on every top-level page.
 * ─────────────────────────────────────────────────────────
 * Layout (single row, 64 px):
 *   [icon] SMIN                nav links               [theme] [Sign in / user]
 *
 * Design tokens: uses --header-bg / --header-fg / --header-active-bg etc.
 * from index.css. This gives the header the app's own brand color in both
 * light and dark modes, and lets other Shanvi apps recolour by swapping
 * these tokens alone.
 */
export default function SminHeader({
  variant = "marketing",
  navLinks = null,
  ctaOnClick = null,
  ctaLabel = null,
  compact = false,
  forceGuest = false,
}) {
  const nav = useNavigate();
  const { user, logout } = useAuth() || {};
  const isAuthed = Boolean(user) && !forceGuest;
  const H = compact ? "h-14" : "h-16";

  const onCta = () => {
    if (ctaOnClick) return ctaOnClick();
    if (isAuthed) return nav("/dashboard");
    return nav("/");
  };

  return (
    <header
      data-testid="smin-header"
      className="sticky top-0 z-30 border-b backdrop-blur-md supports-[backdrop-filter]:bg-white/85"
      style={{
        background:    "hsl(var(--header-bg))",
        color:         "hsl(var(--header-fg))",
        borderColor:   "hsl(var(--header-border))",
      }}
    >
      <div className={`relative mx-auto max-w-7xl px-6 md:px-8 ${H} flex items-center justify-between gap-4`}>
        {/* ───── LEFT · logo (clickable → /landing on every page) ───── */}
        <Link
          to="/landing"
          data-testid="header-brand"
          className="flex items-center group shrink-0"
          aria-label="SMIN"
        >
          <img
            src="/branding/smin-logo-horizontal.png?v=9"
            alt="SMIN"
            draggable={false}
            className="h-11 w-auto object-contain select-none shrink-0"
            style={{ imageRendering: "auto" }}
          />
        </Link>

        {/* ───── CENTER · navigation (evenly spaced, animated underline on hover) ───── */}
        {variant === "marketing" && navLinks && navLinks.length > 0 && (
          <nav
            data-testid="header-nav"
            className="hidden lg:flex items-center gap-8 flex-1 justify-center mx-6"
          >
            {navLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`header-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative group text-sm font-medium py-2 whitespace-nowrap transition-colors text-neutral-600 hover:text-neutral-900"
              >
                {l.label}
                <span
                  className="pointer-events-none absolute left-0 -bottom-0.5 h-0.5 w-full origin-center scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  style={{ background: "#4CAF50" }}
                />
              </Link>
            ))}
          </nav>
        )}

        {/* ───── RIGHT · auth actions ───── */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">

          {!isAuthed && (
            <>
              {/* Login — ghost button, subtle */}
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="hidden md:inline-flex items-center h-10 px-4 rounded-md font-semibold text-sm tracking-tight text-neutral-700 hover:text-[#4CAF50] hover:bg-neutral-100 transition-colors"
              >
                Login
              </Link>
              {/* Create Account — primary CTA with arrow, subtle shadow + hover lift */}
              <Link
                to="/signup"
                data-testid="header-create-account-btn"
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md font-semibold text-sm tracking-tight text-white transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                style={{ background: "#4CAF50" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#43a047")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#4CAF50")}
              >
                Create Account
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              {ctaLabel && ctaLabel !== "Sign in" && (
                <button
                  type="button"
                  data-testid="header-signin-cta"
                  onClick={onCta}
                  className="hidden lg:inline-flex items-center gap-1.5 rounded-md h-10 px-5 text-sm font-semibold tracking-tight transition-colors"
                  style={{
                    background: "hsl(var(--secondary))",
                    color:      "hsl(var(--secondary-foreground))",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
                  onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
                >
                  {ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}

          {isAuthed && (
            <>
              <Link
                to="/profile"
                data-testid="header-user-link"
                className="hidden md:flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-3 h-9 rounded-md text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <User className="h-3.5 w-3.5" />
                {user?.name || user?.email || "Account"}
              </Link>
              <button
                type="button"
                data-testid="header-signout"
                onClick={() => { logout?.(); nav("/"); }}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-3 h-9 font-mono text-[11px] uppercase tracking-widest text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Sign out</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
